import { useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/ConfirmCreateShareDialog';

TEXT.DEFINE({
  /** Dialog title warning about sharing restricted items */
  title: "You're about to share restricted items",
  /** Dialog description explaining Share Link access */
  description:
    'Anyone with Share Link access can view. Subfolders will not be shared.',
  /** Button label to cancel the Share creation */
  cancel: 'Cancel',
  /** Button label to confirm and create the Share */
  confirm: 'Share',
});

/**
 * A confirmation dialog component for creating shares.
 *
 * This component displays a dialog asking the user to confirm or cancel
 * the creation of a share. It uses the AlertDialog component to present
 * the confirmation interface.
 *
 * @param props - Component props
 * @param props.isShowing - Whether the dialog is currently visible
 * @param props.onConfirm - Callback function that receives the user's choice
 *                          (true for confirm, false for cancel)
 * @returns A React component that renders the confirmation dialog
 */
export const ConfirmCreateShareDialog = ({
  isShowing,
  onConfirm,
}: {
  isShowing: boolean;
  onConfirm: (result: boolean) => void;
}) => {
  // Extract TEXT properties to avoid calling hooks inside useMemo
  const textCancel = TEXT.cancel;
  const textConfirm = TEXT.confirm;

  const actions = useMemo(() => {
    return (
      <>
        <AlertDialogSecondaryAction onClick={() => onConfirm(false)}>
          {textCancel}
        </AlertDialogSecondaryAction>
        <AlertDialogPrimaryAction onClick={() => onConfirm(true)}>
          {textConfirm}
        </AlertDialogPrimaryAction>
      </>
    );
  }, [onConfirm, textCancel, textConfirm]);

  return (
    <AlertDialog
      actions={actions}
      aria-labelledby="title"
      details={TEXT.description}
      hasOutermostTheme={true}
      isShowing={isShowing}
      title={TEXT.title}
      variant="notice"
    />
  );
};
